<template>
  <div class="user-menu">
    <sl-dropdown placement="right">
      <div slot="trigger" class="user-menu-button">
        <span class="img-border">
          <img src="/public/avatar.webp" alt="user avatar" />
        </span>
        <span class="user-menu-button-label">Settings</span>
      </div>
      <div class="dropdown-content">
        <h3 class="user-name">{{ userProfileStore.fullName }}</h3>
        <mc-button
          clear
          icon="icon-user-tie"
          @click="navigateToManagerPortal"
          :class="['navigate-to-portal', SHOW_UNSAVED_CHANGES_CSS_CLASS_NAME]"
        >
          Manager Portal
        </mc-button>
        <div v-if="appVersion" class="app-version">
          Spokn AI Version: {{ appVersion }}
        </div>
      </div>
    </sl-dropdown>
  </div>
</template>

<script setup lang="ts">
import { useRuntimeConfig } from "nuxt/app";
import { RuntimeConfig } from "nuxt/schema";
import "@shoelace-style/shoelace/dist/components/dropdown/dropdown.js";
import McButton from "@/components/ui/McButton.vue";
import { useUserProfileStore } from "@/store/userProfileStore";
import { SHOW_UNSAVED_CHANGES_CSS_CLASS_NAME } from "@/constants/cssClassNames";

const runtimeConfig: RuntimeConfig = useRuntimeConfig();
const userProfileStore = useUserProfileStore();

const appVersion = runtimeConfig.public.appVersion;

const navigateToManagerPortal = (): void => {
  const portalLink = userProfileStore.portalLink ?? "/";
  window.location.href = portalLink;
};
</script>

<style scoped lang="scss">
.user-menu {
  --user-menu-height: 42px;
  --user-image-border-size: 32px;

  display: flex;
  align-items: center;
  height: var(--user-menu-height);
  border-radius: 3px;
  user-select: none;
  cursor: pointer;

  .user-menu-button {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    color: var(--colour-text-neutral-default-dark);
    height: var(--user-menu-height);

    .img-border {
      width: var(--user-image-border-size);
      height: var(--user-image-border-size);
      border-radius: 100%;
      background: var(--colour-border-neutral-default);
      padding: 2px;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;

      img {
        border-radius: 100%;
        object-fit: cover;
        display: block;
        cursor: pointer;
      }
    }

    .user-menu-button-label {
      display: block;
      font-size: var(--font-size-headers-x-small);
    }
  }

  &:hover {
    background: var(--colour-background-neutral-default-a-dark);
  }

  .dropdown-content {
    min-width: 193px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    background: var(--base-dark-purple);
    padding: 12px;
    box-shadow: var(--shadow-2);
    margin-bottom: 10px;
    margin-left: 30px;
    cursor: text;

    .user-name {
      font-size: var(--font-size-headers-large);
      font-weight: var(--font-weight-body-regular);
      color: var(--colour-text-neutral-default-dark);
      margin: 0;
      margin-left: 12px;
    }

    .navigate-to-portal {
      --button-background-color: var(--colour-background-brand-default);
      --button-background-color-hover: var(--colour-background-neutral-hover-a-dark);
      --button-background-color-active: var(--colour-background-neutral-hover-a-dark);
      --button-background-color-focus: var(--colour-background-neutral-hover-a-dark);
      --button-icon-font-size: 16px;

      width: 100%;
      justify-content: unset;
    }

    .app-version {
      font-size: var(--font-size-body-small);
      font-weight: var(--font-weight-body-medium);
      color: var(--colour-text-neutral-subtle-dark);
      padding-left: 8px;
    }
  }

  sl-dropdown {
    width: 100%;

    &[open] .img-border {
      background: var(--base-bright-pink);
    }
  }
}
</style>